import { queryKeys, useHttpClient } from '@repo/api-client/react';
import {
  UserSchema,
  type UpdateMeRequest,
} from '@repo/api-client/dtos';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { patchJSON } from '../internal/http';
import { putImage, type UploadableImage } from '../internal/imageUpload';

/** Patch the signed-in user's profile fields. */
export function useUpdateUserProfile() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (updates: UpdateMeRequest) =>
      patchJSON(http, '/users/me', updates, UserSchema),
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKeys.user.me(), profile);
    },
  });

  return {
    updateProfile: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Multipart upload for the signed-in user's avatar.
 *
 * Pass the local image picked by `expo-image-picker`; the hook
 * builds the `multipart/form-data` body with a single `file` field,
 * `PUT`s it to `/users/me/avatar`, and replaces the cached profile
 * with the server's response so screens re-render the new avatar
 * immediately.
 */
export function useUploadAvatar() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (file: UploadableImage) =>
      putImage(http, '/users/me/avatar', file, UserSchema),
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKeys.user.me(), profile);
    },
  });

  return {
    uploadAvatar: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
